import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowRightLeft, Info, Users } from "lucide-react";
import type { MoveWarning } from "@/lib/reservationMoveWarnings";

interface SwapSide {
  customerName: string;
  persons: number;
  time: string;
  currentTableName: string;
  newTableName: string;
}

interface SwapConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  a: SwapSide;
  b: SwapSide;
  aWarnings: MoveWarning[];
  bWarnings: MoveWarning[];
  onConfirm: () => void;
  isSubmitting?: boolean;
}

/**
 * Confirmation step shown before invoking the swap RPC.
 * Displays A ⇄ B summary and the symmetric warnings computed by
 * computeSwapWarnings. Blocking warnings disable the confirm button.
 */
export const SwapConfirmDialog = ({
  open,
  onOpenChange,
  a,
  b,
  aWarnings,
  bWarnings,
  onConfirm,
  isSubmitting,
}: SwapConfirmDialogProps) => {
  const blocking =
    aWarnings.some((w) => w.level === "block") ||
    bWarnings.some((w) => w.level === "block");

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Confirmer l'échange de table
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-1 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <SideCard side={a} warnings={aWarnings} />
                <SideCard side={b} warnings={bWarnings} />
              </div>
              <p className="text-xs text-muted-foreground">
                L'échange est atomique : les deux assignations sont mises à jour
                en même temps.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              if (blocking || isSubmitting) return;
              onConfirm();
            }}
            disabled={blocking || isSubmitting}
          >
            {blocking ? "Action bloquée" : "Confirmer l'échange"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const SideCard = ({ side, warnings }: { side: SwapSide; warnings: MoveWarning[] }) => (
  <div className="rounded-md border bg-muted/30 p-3 space-y-2">
    <div className="font-medium text-foreground truncate">{side.customerName}</div>
    <div className="text-xs text-muted-foreground flex items-center gap-2">
      <Users className="w-3 h-3" /> {side.persons} pers. · {side.time}
    </div>
    <div className="text-xs space-y-0.5">
      <div className="text-muted-foreground line-through">{side.currentTableName}</div>
      <div className="font-medium text-foreground flex items-center gap-1">
        <ArrowRightLeft className="w-3 h-3" /> {side.newTableName}
        <Badge variant="secondary" className="text-[10px] ml-1">
          après échange
        </Badge>
      </div>
    </div>
    {warnings.length > 0 && (
      <div className="space-y-1 pt-1">
        {warnings.map((w, i) => (
          <WarningRow key={i} warning={w} />
        ))}
      </div>
    )}
  </div>
);

const WarningRow = ({ warning }: { warning: MoveWarning }) => {
  const styles =
    warning.level === "block"
      ? "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300"
      : warning.level === "warn"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300";
  const Icon = warning.level === "info" ? Info : AlertTriangle;
  return (
    <div className={`flex items-start gap-1.5 rounded border px-2 py-1 text-[11px] ${styles}`}>
      <Icon className="w-3 h-3 mt-0.5 shrink-0" />
      <span>{warning.message}</span>
    </div>
  );
};
